import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, extension_id } from '../utils/api';
import { FaArrowLeft, FaShoppingCart, FaUser, FaCalendar, FaDollarSign } from 'react-icons/fa';

const CartDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cart, setCart] = useState(null);
  const [cartType, setCartType] = useState(null); // 'customer' | 'order'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderNote, setOrderNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    const fetchCartDetails = async () => {
      if (!user?.id) return;

      try {
        console.log('Fetching cart details for cart ID:', id);

        // First try order-carts endpoint (for order carts from "In Processing" tab)
        let response = await fetch(`${API_URL}/customers/${user.id}/order-carts/${id}?extensionId=${extension_id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });
        let resolvedType = 'order';

        // If not found in order-carts, try regular carts endpoint (for draft carts)
        if (!response.ok && response.status === 404) {
          console.log('Not found in order-carts, trying regular carts...');
          response = await fetch(`${API_URL}/customers/${user.id}/carts/${id}?extensionId=${extension_id}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
          });
          resolvedType = 'customer';
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch cart: ${response.status}`);
        }

        const data = await response.json();
        console.log('Cart data received:', data);

        if (data.success) {
          // Handle both regular carts (data.customerCart) and order carts (data.orderCart)
          const cartData = data.customerCart || data.orderCart;
          if (cartData) {
            // Normalize the data structure for order carts
            if (data.orderCart) {
              cartData.items = cartData.cart_items; // Map cart_items to items
              cartData.total_amount = cartData.customer_cart_total || cartData.cart_total; // Map total
              cartData.subtotal = cartData.customer_cart_subtotal;
              // Add discount and tax from root level if available
              if (data.discount) cartData.discount = data.discount;
              if (data.tax) cartData.tax = data.tax;
            }
            setCart(cartData);
            setCartType(resolvedType);
            setOrderNote(cartData.order_note || '');
          } else {
            throw new Error('Cart data not found in response');
          }
        } else {
          throw new Error('Failed to load cart data');
        }
      } catch (error) {
        console.error('Error fetching cart details:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCartDetails();
  }, [id, user]);

  const handleSaveOrderNote = async () => {
    setSavingNote(true);
    try {
      const url = cartType === 'order'
        ? `${API_URL}/orders/cart/${id}`
        : `${API_URL}/customers/${user.id}/carts/${id}`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ order_note: orderNote }),
      });
      if (!response.ok) {
        throw new Error(`Failed to save note: ${response.status}`);
      }
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to save note');
      }
      setCart((prev) => (prev ? { ...prev, order_note: orderNote } : prev));
    } catch (err) {
      console.error('Error saving order note:', err);
      alert(err.message || 'Failed to save order note');
    } finally {
      setSavingNote(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-sm">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-gray-600 mt-4 text-center">Loading cart details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-sm text-center">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Cart</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!cart) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-sm text-center">
          <div className="text-gray-400 text-4xl mb-4">📭</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Cart Not Found</h2>
          <p className="text-gray-600 mb-4">The cart you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors mr-4"
          >
            <FaArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Cart Details</h1>
        </div>

        {/* Cart Info Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center">
              <div className="bg-blue-100 p-3 rounded-lg mr-4">
                <FaShoppingCart className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Cart #{cart.id}</h2>
                <p className="text-gray-600">
                  <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                    cart.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {cart.status || 'Draft'}
                  </span>
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(cart.total_amount)}</p>
              <p className="text-sm text-gray-500">Total Amount</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
            <div className="flex items-center">
              <FaCalendar className="w-4 h-4 text-gray-400 mr-2" />
              <div>
                <p className="text-sm text-gray-500">Created</p>
                <p className="font-medium">{formatDate(cart.created_at)}</p>
              </div>
            </div>
            <div className="flex items-center">
              <FaUser className="w-4 h-4 text-gray-400 mr-2" />
              <div>
                <p className="text-sm text-gray-500">Customer</p>
                <p className="font-medium">
                  {cart.customer?.name || `${cart.customer?.first_name || ''} ${cart.customer?.last_name || ''}`.trim()}
                </p>
              </div>
            </div>
            <div className="flex items-center">
              <FaDollarSign className="w-4 h-4 text-gray-400 mr-2" />
              <div>
                <p className="text-sm text-gray-500">Items</p>
                <p className="font-medium">{cart.items?.length || 0} items</p>
              </div>
            </div>
          </div>

          <div className="pt-4 mt-4 border-t border-gray-200">
            <label className="block text-sm font-medium text-gray-500 mb-2">Ship To</label>
            <textarea
              rows={3}
              value={orderNote}
              onChange={(e) => setOrderNote(e.target.value)}
              placeholder="Add notes for this order..."
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y"
            />
            <div className="flex justify-end mt-2">
              <button
                onClick={handleSaveOrderNote}
                disabled={savingNote}
                className="bg-blue-500 text-white px-4 py-1.5 rounded text-sm hover:bg-blue-600 disabled:opacity-50"
              >
                {savingNote ? 'Saving...' : 'Save Note'}
              </button>
            </div>
          </div>
        </div>

        {/* Customer Information */}
        {cart.customer && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Name</label>
                <p className="mt-1">
                  {cart.customer.name || `${cart.customer.first_name || ''} ${cart.customer.last_name || ''}`.trim()}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Email</label>
                <p className="mt-1">{cart.customer.email || 'N/A'}</p>
              </div>
              {(cart.customer.phone_number || cart.customer.phone) && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Phone</label>
                  <p className="mt-1">{cart.customer.phone_number || cart.customer.phone}</p>
                </div>
              )}
              {cart.customer.customer_location && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Location</label>
                  <p className="mt-1">{cart.customer.customer_location}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Cart Items */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Cart Items</h3>
          {cart.items && cart.items.length > 0 ? (
            <div className="space-y-4">
              {cart.items.map((item, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">
                        {item.product?.name || item.product?.product_name || `Item #${item.id}`}
                      </h4>
                      {item.product?.sku && (
                        <p className="text-sm text-gray-500">SKU: {item.product.sku}</p>
                      )}
                      <div className="mt-2 text-sm text-gray-600">
                        <p>Quantity: {item.quantity}</p>
                        <p>Unit Price: {formatCurrency(item.customer_product_price || item.product_price || item.price)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">
                        {formatCurrency(item.customer_item_total || item.item_total || (item.price * item.quantity))}
                      </p>
                    </div>
                  </div>
                  
                  {/* Show inputs if available */}
                  {item.inputs && ((Array.isArray(item.inputs) && item.inputs.length > 0) || (!Array.isArray(item.inputs) && Object.keys(item.inputs).length > 0)) && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-sm font-medium text-gray-700 mb-2">Specifications:</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {Array.isArray(item.inputs) 
                          ? item.inputs.map((input, idx) => (
                              <div key={idx}>
                                <span className="text-gray-500">{input.inputName}:</span>
                                <span className="ml-1 text-gray-900">{input.value}</span>
                              </div>
                            ))
                          : Object.entries(item.inputs).map(([key, value]) => (
                              <div key={key}>
                                <span className="text-gray-500">{key}:</span>
                                <span className="ml-1 text-gray-900">{value}</span>
                              </div>
                            ))
                        }
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 text-4xl mb-4">📦</div>
              <p className="text-gray-500">No items in this cart</p>
            </div>
          )}
        </div>

        {/* Summary */}
        {cart.discount || cart.tax ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span>{formatCurrency(cart.subtotal || cart.total_amount)}</span>
              </div>
              {/* {cart.discount && (
                <div className="flex justify-between text-green-600">
                  <span>Discount ({cart.discount.discount_type}):</span>
                  <span>-{cart.discount.discount_type === 'percentage' ? `${cart.discount.discount_rate}%` : formatCurrency(cart.discount.discount_rate)}</span>
                </div>
              )} */}
              {cart.tax && (
                <div className="flex justify-between">
                  <span>Tax:</span>
                  <span>{formatCurrency(cart.tax)}</span>
                </div>
              )}
              <div className="border-t border-gray-200 pt-2">
                <div className="flex justify-between font-semibold text-lg">
                  <span>Total:</span>
                  <span>{formatCurrency(cart.total_amount)}</span>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CartDetails; 